import { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Lock, Database, ChevronDown, ChevronUp, Calendar, Sparkles } from "lucide-react";
import Stars from "@/components/Stars";
import StatusBadge from "@/components/StatusBadge";
import { HitTile, AvgTile, HitChart, GameLog, Unavailable } from "@/components/prop/PropStatParts";
import { useTimezone, fmtEvent, tzAbbr } from "@/lib/time";
import Odds from "@/components/Odds";

function Headshot({ pick, player }) {
  const name = player?.name || pick.player || "?";
  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-[#00E676]/40 bg-[#0F131D] sm:h-24 sm:w-24" data-testid="prop-player-photo">
      {pick.player_photo_url ? (
        <img src={pick.player_photo_url} alt={name} className="h-full w-full object-cover object-top" />
      ) : (
        <div className="flex h-full w-full items-center justify-center font-display text-2xl font-extrabold text-[#00E676]">{initials}</div>
      )}
      {player?.jersey && <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 font-mono text-[10px] text-white">#{player.jersey}</span>}
    </div>
  );
}

function SelectionBanner({ pick, stats }) {
  const side = (pick.prop_side || "").toUpperCase();
  const label = stats?.market_label || pick.selection;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#00E676]/40 bg-[#00E676]/10 px-4 py-3" data-testid="prop-selection-banner">
      <span className="font-display text-2xl font-extrabold uppercase tracking-wide text-[#00E676] sm:text-3xl">{side} {pick.prop_line}</span>
      <span className="font-display text-sm font-bold uppercase tracking-wider text-white">{label}</span>
      <span className="ml-auto font-mono text-xs text-slate-300">@ <Odds value={pick.odds} /> · {pick.units}U</span>
    </div>
  );
}

function StatsBody({ pick, stats }) {
  const [showLog, setShowLog] = useState(false);
  if (!stats) return <Unavailable reason="This pick has no player-prop configuration" />;
  if (!stats.available) return <Unavailable reason={stats.reason} tier={stats.tier_required} />;
  const { hit_rates: hr, averages: av, vs_opponent: vs } = stats;
  return (
    <div className="space-y-6" data-testid="prop-stats-body">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <HitTile label="Last 5" rate={hr.l5} testid="prop-hit-l5" />
        <HitTile label="Last 10" rate={hr.l10} testid="prop-hit-l10" />
        <HitTile label={`Season (${stats.sample_size})`} rate={hr.season} testid="prop-hit-season" />
      </div>
      <div>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><span className="sm:hidden">Last {Math.min(stats.games.length, 10)} games</span><span className="hidden sm:inline">Last {Math.min(stats.games.length, 15)} games</span> · {stats.market_label} vs line</p>
        <HitChart games={stats.games} line={stats.line} />
      </div>
      <div className="grid gap-x-8 sm:grid-cols-2">
        <div>
          <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Averages</p>
          <AvgTile label="Last 5" value={av.l5} line={stats.line} side={stats.side} />
          <AvgTile label="Last 10" value={av.l10} line={stats.line} side={stats.side} />
          <AvgTile label="Season" value={av.season} line={stats.line} side={stats.side} />
          <AvgTile label="Minutes (L10)" value={av.minutes_l10} />
        </div>
        <div data-testid="prop-vs-opponent" className="mt-5 sm:mt-0">
          <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Vs {stats.opponent?.abbr || "opponent"}{stats.seasons.length > 1 ? " (last 2 seasons)" : " (this season)"}</p>
          {vs ? (
            <>
              <AvgTile label={`Avg in ${vs.games.length} game${vs.games.length > 1 ? "s" : ""}`} value={vs.avg} line={stats.line} side={stats.side} />
              <AvgTile label="Hit rate" value={vs.pct == null ? "—" : `${vs.pct}% (${vs.hits}/${vs.games.length - vs.pushes})`} />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {vs.games.map((g) => <span key={g.game_id} className={`rounded px-2 py-0.5 font-mono text-[11px] font-bold ${g.hit === true ? "bg-[#00E676]/15 text-[#00E676]" : g.hit === false ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-slate-500/20 text-slate-300"}`}>{g.date?.slice(5)} · {g.value}</span>)}
              </div>
            </>
          ) : (
            <p className="py-2 text-xs text-slate-500">{stats.opponent ? "No completed games against this opponent in the sampled seasons." : "Opponent not identified from the fixture."}</p>
          )}
        </div>
      </div>
      <button type="button" data-testid="prop-toggle-log" onClick={() => setShowLog((v) => !v)} className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#2A364F] py-2 font-display text-xs font-bold uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00E676]/50 hover:text-[#00E676]">
        {showLog ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showLog ? "Hide" : "Show"} game-by-game log
      </button>
      {showLog && <GameLog games={stats.games} marketLabel={stats.market_label} />}
      <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-600" data-testid="prop-stats-source">
        <Database size={11} /> Calculated from official box scores via BALLDONTLIE · updated {new Date(stats.computed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </p>
    </div>
  );
}

function Analysis({ pick }) {
  const [open, setOpen] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches);
  const blocks = [["Why this pick", pick.why || pick.analysis], ["Risks", pick.risks], ["Final assessment", pick.final_assessment]].filter(([, v]) => v);
  if (!blocks.length && !pick.key_factors?.length) return null;
  return (
    <div className="rounded-xl border border-[#1E2738] bg-[#0F131D] p-4 sm:p-5" data-testid="prop-analysis">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="prop-analysis-toggle" className="flex w-full items-center justify-between font-display text-xs font-bold uppercase tracking-wider text-[#00E676]">
        Owner's analysis {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          {blocks.map(([t, v]) => (
            <div key={t}><p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t}</p><p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed text-slate-300 sm:text-sm">{v}</p></div>
          ))}
          {pick.key_factors?.length > 0 && (
            <ul className="grid gap-1.5 sm:grid-cols-2">{pick.key_factors.map((k) => <li key={k} className="flex gap-2 text-[15px] text-slate-300 sm:text-sm"><span className="text-[#00E676]">▸</span>{k}</li>)}</ul>
          )}
        </div>
      )}
    </div>
  );
}

export function PropStatCard({ pick, stats, locked = false, onUnlock, index = 0 }) {
  const { tz } = useTimezone();
  const player = stats?.player;
  const eventIso = pick.event_start_utc || pick.game_time;
  const [away, home] = (pick.game || "").split(" @ ");
  const opp = stats?.opponent?.name || (player?.team_name === home ? away : home);
  return (
    <motion.article data-testid={`prop-card-${pick.id}`} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: index * 0.05 }}
      className="card-mp relative overflow-hidden p-5 sm:p-7">
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#00E676]/10 blur-3xl" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span className="tag border border-[#2A364F] bg-[#0F131D] text-slate-300">🏀 NBA · Player Prop</span>
        {pick.visibility === "free" ? <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">Free Pick</span> : <span className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"><Crown size={10} /> VIP</span>}
        {pick.best_bet && <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]"><Sparkles size={10} /> Best Bet</span>}
        <span className="ml-auto"><StatusBadge status={pick.status === "void" ? "push" : pick.status} profit={pick.profit} /></span>
      </div>

      <div className="flex gap-4 sm:gap-5">
        <Headshot pick={pick} player={player} />
        <div className="min-w-0 flex-1">
          <h3 className={`font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-white sm:text-4xl ${locked ? "blur-[4px] select-none" : ""}`} data-testid="prop-player-name">{player?.name || pick.player}</h3>
          <p className="mt-1.5 font-mono text-xs text-slate-400">{player ? `${player.team_name} · ${player.position}` : pick.league}{opp ? <span className="text-slate-300"> vs {opp}</span> : null}</p>
          <p className="mt-2 flex items-center gap-1.5 font-mono text-[11px] text-[#00E676]/90" data-testid="prop-event-time"><Calendar size={12} /> {fmtEvent(eventIso, tz)} {tzAbbr(tz, eventIso)}</p>
          <div className="mt-2 flex items-center gap-2"><span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Confidence</span><Stars value={pick.confidence} /></div>
        </div>
      </div>

      <div className="mt-5 space-y-6">
        {locked ? (
          <div className="relative overflow-hidden rounded-xl border border-[#F59E0B]/30 p-6 text-center" data-testid="prop-locked">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-[#0B0E14]" />
            <Lock size={22} className="mx-auto text-[#F59E0B]" />
            <p className="mt-2 font-display text-base font-bold uppercase tracking-wider text-white">VIP statistical breakdown</p>
            <p className="mt-1 text-sm text-slate-400">{pick.analysis_preview || "Hit rates, game log, matchup history and full analysis are reserved for VIP members."}</p>
            <button data-testid={`unlock-analysis-btn-${pick.id}`} onClick={onUnlock} className="btn-primary mt-4 !px-5 !py-2.5"><Crown size={14} /> Unlock with VIP</button>
          </div>
        ) : (
          <>
            <SelectionBanner pick={pick} stats={stats} />
            <StatsBody pick={pick} stats={stats} />
            <Analysis pick={pick} />
          </>
        )}
      </div>
    </motion.article>
  );
}

export default PropStatCard;
